/**
 * 游戏中心
 * ------------------------------------------------------------
 * 游戏与练习分离：这里是独立板块，但读的是同一份教材数据，
 * 判分也复用统一的 grader，所以游戏成绩同样进入学习数据。
 *
 * 游戏模板可插拔：注册在 src/games/<id>/index.js，元数据在 data/games.json。
 */
import { state } from '../core/store.js';
import { listGames, plannedGames, gameRegistry, GameBase } from '../games/index.js';
import { getUnitContent } from '../services/dataSource.js';
import { priorityFor } from '../engines/sampler.js';
import { PracticeSession } from '../engines/practiceEngine.js';
import { wordGenerator } from '../engines/generators/wordLevels.js';
import { sentenceGenerator } from '../engines/generators/sentenceLevels.js';
import { saveSession } from '../services/records.js';
import { settleSession } from '../services/rewards.js';
import { card, emptyState, tag, confirmDialog } from '../components/ui.js';
import { navigate } from '../core/router.js';
import { escapeHtml, toast, fmtDuration } from '../utils/index.js';

export default async function renderGames(mount, params = {}) {
  const games = await listGames();
  const planned = await plannedGames();
  const gameId = params.gameId;

  if (gameId) {
    const entry = gameRegistry.get(gameId);
    if (!entry) {
      mount.innerHTML = emptyState('🎮', '游戏不存在', `未找到 ${gameId}`, '<a class="btn btn-primary" href="#/games">返回游戏中心</a>');
      return;
    }
    return runGame(mount, entry);
  }

  mount.innerHTML = `
    <div class="page-head">
      <h1>🎮 游戏中心</h1>
      <p class="lead">用游戏的方式巩固单词和句子。游戏成绩同样计入学习数据与成就。</p>
    </div>

    ${games.length ? `<div class="grid auto">
      ${games.map(g => `
        <button class="game-card ${g.available ? '' : 'disabled'}" data-game="${g.id}" type="button" ${g.available ? '' : 'disabled'}>
          <div class="game-thumb">${g.icon || '🎮'}</div>
          <div class="grow">
            <div class="row between"><strong>${escapeHtml(g.name)}</strong><span class="muted" style="font-size:11px">${escapeHtml(g.difficulty || '')}</span></div>
            <div class="muted" style="font-size:12px;margin-top:2px">${escapeHtml(g.desc || '')}</div>
            <div class="mt-4">${(g.supports || []).map(s => `<span class="tag">${supportLabel(s)}</span>`).join('')}</div>
          </div>
        </button>`).join('')}
    </div>` : emptyState('🎮', '还没有可玩的游戏', '在 src/games/ 下注册新游戏即可')}

    ${planned.length ? card(`
      <div class="grid auto">
        ${planned.map(g => `
          <div class="game-card disabled">
            <div class="game-thumb" style="filter:grayscale(.5);opacity:.7">${g.icon || '🎮'}</div>
            <div class="grow">
              <div class="row between"><strong>${escapeHtml(g.name)}</strong>${tag('敬请期待', 'orange')}</div>
              <div class="muted" style="font-size:12px">${escapeHtml(g.desc || '')}</div>
            </div>
          </div>`).join('')}
      </div>`, { title: '即将上线' }) : ''}`;

  mount.querySelectorAll('[data-game]').forEach(btn => {
    if (btn.disabled) return;
    btn.addEventListener('click', () => navigate(`#/games/${btn.dataset.game}`));
  });
}

function supportLabel(s) {
  return { word: '单词', sentence: '句子', grammar: '语法', mixed: '混合' }[s] || s;
}

/* ------------------------------ 运行游戏 ------------------------------ */

async function runGame(mount, entry) {
  const { meta } = entry;
  const unitId = state.selection.unitId;
  const textbookId = state.selection.textbookId;

  if (!unitId) {
    mount.innerHTML = emptyState('📚', '请先选择单元', '游戏需要读取单元内容出题', '<a class="btn btn-primary" href="#/textbooks">去教材管理</a>');
    return;
  }

  const content = await getUnitContent(textbookId, unitId);
  const priority = await priorityFor(unitId, 'mixed');

  // 用练习生成器出题：单词 8 题 + 句子 4 题，共 12 关
  const questions = [
    ...wordGenerator.build({ content, level: 7, count: 8, priority }),
    ...sentenceGenerator.build({ content, level: 5, count: 4, priority }),
  ];
  if (!questions.length) {
    mount.innerHTML = emptyState('📭', '这个单元没有可用的题目', '先导入内容再来玩', '<a class="btn btn-primary" href="#/import">去导入</a>');
    return;
  }

  mount.innerHTML = `<div class="game-wrap" id="game-root"></div>`;
  const root = mount.querySelector('#game-root');

  const game = entry.create({
    root,
    questions,
    content,
    onFinish: async (result) => {
      if (result.aborted) { navigate('#/games'); return; }
      await settleGame(mount, root, meta, result, unitId, textbookId);
    },
  });
  game.start();

  // 离开页面时清理定时器
  const onHash = () => { game.destroy?.(); window.removeEventListener('hashchange', onHash); };
  window.addEventListener('hashchange', onHash);
  return onHash;
}

async function settleGame(mount, root, meta, result, unitId, textbookId) {
  const total = result.total || 0;
  const correct = result.correct || 0;
  const score = total ? Math.round((correct / total) * 100) : 0;

  const summary = {
    id: `game-${Date.now().toString(36)}`,
    module: 'game',
    title: meta.name,
    unitId,
    textbookId,
    total,
    correct,
    independent: correct,
    hinted: 0,
    failed: total - correct,
    accuracy: score,
    score,
    bySkill: sumBy(result.records, 'skill'),
    byLevel: sumBy(result.records, 'level'),
    startedAt: new Date().toISOString(),
    endedAt: new Date().toISOString(),
    durationMs: 0,
    records: result.records || [],
  };

  let reward = { xp: 0, unlocked: [] };
  try {
    // 与 session.js 保持一致：先落库再判成就（成就统计依赖本次记录）
    await saveSession(summary);
    reward = await settleSession(summary);
  } catch (err) {
    console.error('[game] 保存失败', err);
  }

  root.innerHTML = `
    <div class="result-wrap">
      <div class="result-hero ${score >= 80 ? 'good' : score >= 60 ? 'mid' : 'low'}">
        <div class="result-score">${score}</div>
        <div class="result-sub">救出 ${result.saved ?? correct} 只小精灵 · 最高连击 ${result.maxCombo || 0} · ${total} 题答对 ${correct} 题</div>
      </div>
      <div class="grid c2 mt-8">
        ${card(`<div class="xp-gain">+${reward.xp} <small>XP</small></div>`, { title: '获得经验' })}
        ${card(reward.unlocked?.length
          ? reward.unlocked.map(a => `<div class="row" style="gap:8px;margin-bottom:6px"><span>${a.icon}</span><b>${escapeHtml(a.name)}</b></div>`).join('')
          : '<p class="muted">暂无新成就</p>', { title: '成就' })}
      </div>
      <div class="row mt-8" style="gap:10px;justify-content:center">
        <button class="btn btn-primary" id="again" type="button">再玩一次</button>
        <a class="btn btn-ghost" href="#/games">返回游戏中心</a>
        <a class="btn btn-ghost" href="#/report">看学习数据</a>
      </div>
    </div>`;

  mount.querySelector('#again')?.addEventListener('click', () => navigate(`#/games/${meta.id}`));
}

function sumBy(records = [], key) {
  const out = {};
  records.forEach(r => {
    const k = r[key] ?? '?';
    out[k] = out[k] || { total: 0, correct: 0 };
    out[k].total += 1;
    if (r.correct) out[k].correct += 1;
  });
  return out;
}
